import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Play, Users, Calendar } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { Card } from "../ui/card";

export function InstructorBio() {
  const { t } = useTranslation();

  // Datos encapsulados del instructor
  const instructorData = {
    name: "Pedro Cifuentes",
    bioMarkdown: "Pedro Cifuentes lleva más de siete años especializándose en plantas medicinales y biotecnología aplicada. Ha colaborado con más de 40 universidades y centros de investigación españoles de referencia, incluyendo Cicytex, CRAG, CSIC-CEBAS y CBGP.\n\nFormado inicialmente en Plant Cell Technology con Bill Graham, ahora colabora como miembro de PCT Evangelist, Pedro se ha dedicado a llevar el cultivo in vitro del laboratorio a la práctica real. Ha trabajado en proyectos de innovación hortícola con Bioled Lighting y ha aplicado estas técnicas en situaciones reales de producción.\n\nEn los últimos años ha formado a más de 100 cultivadores y técnicos en metodologías avanzadas de conservación, saneamiento y multiplicación de genéticas. Su enfoque combina rigor científico con explicaciones claras y aplicaciones prácticas que los alumnos pueden implementar desde el primer día.\n\nComo divulgador, mantiene una comunidad de más de 2.700 suscriptores en YouTube y 14.000 seguidores en Instagram, donde comparte conocimiento técnico de forma accesible. Pedro se caracteriza por su capacidad para simplificar conceptos complejos y resolver dudas de manera directa.\n\nSu objetivo es formar profesionales que no solo comprendan el cultivo in vitro, sino que sepan aplicarlo con éxito para obtener resultados tangibles en sus proyectos.",
    social: [
      { type: "youtube" as const, url: "https://www.youtube.com/@rebelgenomics" },
      { type: "linkedin" as const, url: "https://www.linkedin.com/in/pedrocifuentes" },
      { type: "calendar" as const, url: "https://calendly.com/pedro-cifuentes-rebelgenomics/30min" }
    ]
  };

  // Estructura clave-valor para los bloques de contenido
  const bioBlocks: Record<string, { titleKey: string; contentKey: string; image: string }> = {
    "experiencia": {
      titleKey: "breeders.professor.bio.experiencia.title",
      contentKey: "breeders.professor.bio.experiencia.content",
      image: "/assets/pedro1.JPG"
    },
    "innovacion": {
      titleKey: "breeders.professor.bio.innovacion.title",
      contentKey: "breeders.professor.bio.innovacion.content",
      image: "/assets/pedro2.png"
    },
    "divulgacion": {
      titleKey: "breeders.professor.bio.divulgacion.title",
      contentKey: "breeders.professor.bio.divulgacion.content",
      image: "/assets/pedro3.png"
    }
  };

  // Array de claves para mantener el orden
  const blockKeys: (keyof typeof bioBlocks)[] = ["experiencia", "innovacion", "divulgacion"];

  const getSocialIcon = (type: string) => {
    switch (type) {
      case "youtube":
        return <Play className="w-8 h-8 text-white" />;
      case "linkedin":
        return <Users className="w-8 h-8 text-white" />;
      case "calendar":
        return <Calendar className="w-8 h-8 text-white" />;
      default:
        return null;
    }
  };

  const getSocialTitle = (type: string) => {
    switch (type) {
      case "youtube":
        return t("breeders.professor.banners.videos.title");
      case "linkedin":
        return t("breeders.professor.banners.linkedin.title");
      case "calendar":
        return t("breeders.professor.banners.consultation.title");
      default:
        return "";
    }
  };

  const getSocialDescription = (type: string) => {
    switch (type) {
      case "youtube":
        return {
          line1: t("breeders.professor.banners.videos.description1"),
          line2: t("breeders.professor.banners.videos.description2"),
          button: t("breeders.professor.banners.videos.button"),
        };
      case "linkedin":
        return {
          line1: t("breeders.professor.banners.linkedin.description1"),
          line2: t("breeders.professor.banners.linkedin.description2"),
          button: t("breeders.professor.banners.linkedin.button"),
        };
      case "calendar":
        return {
          line1: t("breeders.professor.banners.consultation.description1"),
          line2: t("breeders.professor.banners.consultation.description2"),
          button: t("breeders.professor.banners.consultation.button"),
        };
      default:
        return { line1: "", line2: "", button: "" };
    }
  };

  return (
    <section
      id="profesor"
      className="min-h-screen bg-black text-white py-20"
      aria-labelledby="instructor-heading"
    >
      <div className="container mx-auto px-4">
        {/* Título principal */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 id="instructor-heading" className="text-5xl md:text-7xl text-orange-500 mb-4">
            {t("breeders.professor.title1")}
            <br />
            <span className="text-white">PEDRO CIFUENTES</span>
          </h2>
          <h3 className="text-3xl text-orange-400">
            {t("breeders.professor.title2")}
          </h3>
        </motion.div>

        {/* Bloques de texto con imágenes intercaladas */}
        <div className="space-y-12">
          {blockKeys.map((key, index) => {
            const block = bioBlocks[key];
            return (
              <section
                key={key}
                className="bg-black text-white flex items-center py-16"
              >
                <div className="container mx-auto px-4">
                  <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Texto */}
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                      className={index % 2 === 1 ? 'lg:order-2' : ''}
                    >
                      <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
                        {t(block.titleKey)}
                      </h2>
                      <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                        {t(block.contentKey)}
                      </p>
                    </motion.div>

                    {/* Imagen */}
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? 100 : -100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                      className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}
                    >
                      <Card className="bg-gray-900 border-orange-500/30 p-8 rounded-3xl">
                        <ImageWithFallback
                          src={block.image}
                          alt={`${instructorData.name} - ${t(block.titleKey)}`}
                          className="w-full h-80 object-cover rounded-2xl"
                        />
                      </Card>
                    </motion.div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Segunda fila: 3 banners centrados */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {instructorData.social.map((socialItem, index) => {
              const description = getSocialDescription(socialItem.type);
              return (
                <div
                  key={index}
                  className="bg-gradient-to-r from-orange-500 to-red-500 p-8 rounded-2xl border-2 border-orange-400 relative overflow-hidden cursor-pointer group hover:scale-105 transition-transform duration-300"
                  onClick={() => window.open(socialItem.url, "_blank")}
                >
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center mb-4">
                      <div className="bg-white/20 p-4 rounded-full">
                        {getSocialIcon(socialItem.type)}
                      </div>
                    </div>
                    <h3 className="text-white text-center text-2xl mb-2 uppercase tracking-wide">
                      {getSocialTitle(socialItem.type)}
                    </h3>
                    <p className="text-white/90 text-center text-lg">
                      {description.line1}
                    </p>
                    <p className="text-white/90 text-center">
                      {description.line2}
                    </p>
                    <div className="mt-4 text-center">
                      <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-white text-sm uppercase tracking-wider">
                        {description.button}
                      </span>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 -right-2 opacity-20">
                    {getSocialIcon(socialItem.type)}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
